import { cloneCartPayload, ensureCartProductIdentity, ensureCartServiceIdentity, normalizeCartPayload, normalizeNewProductForCartWorkspace } from './cartWorkspaceModel';
import { arrayMove } from '@dnd-kit/sortable';
import { buildCartLayout, CART_LAYOUT_ROW_TYPES, normalizeSectionStyle, normalizeSectionTitle } from './cartLayout';

const SERVICE_MATCH_KEYS = ['cartServiceId', 'id'];
const SECTION_MATCH_KEYS = ['sectionId', 'id'];

const deepClone = (value) => JSON.parse(JSON.stringify(value));
const uniqueWorkspaceId = (prefix) => `${prefix}:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`;


const resolveIdentityValue = (identity, key) => {
  if (identity && typeof identity === 'object') return identity[key];
  return key === 'cartLineId' || key === SERVICE_MATCH_KEYS[0] ? identity : undefined;
};

const findByIdentity = (list = [], identity, keys = []) => list.findIndex((entry) => keys.some((key) => {
  const identityValue = resolveIdentityValue(identity, key);
  return identityValue !== undefined && identityValue !== null && entry?.[key] === identityValue;
}));

const firstPresent = (...values) => values.find((value) => value !== undefined && value !== null && String(value).trim() !== '');
export const getSnapshotOrderItemIdentity = (product = {}) => firstPresent(
  product.persistedOrderItemId,
  product.PersistedOrderItemId,
  product.OrderItemId,
  product.orderItemId,
  product.dbItemId,
  product.DBItemId,
  product.orderProductId,
  product.OrderProductId,
  product.id,
);
export const getSnapshotCartLineIdentity = (product = {}) => typeof product === 'object'
  ? firstPresent(product.cartLineId, product.CartLineId, product.lineId, product.LineId)
  : firstPresent(product);
const matchingProductIndexes = (list = [], identity = {}) => {
  const cartLineId = getSnapshotCartLineIdentity(identity);
  const orderItemId = getSnapshotOrderItemIdentity(identity);
  const indexesFor = (matcher) => list.reduce(
    (indexes, entry, index) => matcher(entry) ? [...indexes, index] : indexes,
    [],
  );
  const cartLineIndexes = cartLineId == null ? [] : indexesFor(
    (entry) => String(getSnapshotCartLineIdentity(entry)) === String(cartLineId),
  );
  if (cartLineIndexes.length === 1) {
    return { indexes: cartLineIndexes, strategy: 'cart-line-id' };
  }

  const orderItemIndexes = orderItemId == null ? [] : indexesFor(
    (entry) => String(getSnapshotOrderItemIdentity(entry)) === String(orderItemId),
  );
  if (orderItemIndexes.length === 1) {
    return { indexes: orderItemIndexes, strategy: 'order-item-id-fallback' };
  }

  return { indexes: [], strategy: 'none' };
};
const findProductByIdentity = (list = [], identity = {}) => {
  const { indexes } = matchingProductIndexes(list, identity);
  return indexes.length === 1 ? indexes[0] : -1;
};

const applySequentialProductSortOrder = (products = [], source = undefined) =>
  products.map((product, index) =>
    ensureCartProductIdentity(
      {
        ...product,
        sortOrder: index,
      },
      index,
      source
      )
  );

const applySequentialSectionSortOrder = (sections = []) =>
  sections.map((section, index) => ({
    ...section,
    title: normalizeSectionTitle(section?.title),
    style: normalizeSectionStyle(section?.style ?? section?.Style),
    sortOrder: index,
  }));

const applyLayoutSortOrders = (payload) => {
  const layout = buildCartLayout({
    products: payload?.Products,
    sections: payload?.cartSections,
  });
  const productSortOrderById = new Map(
    layout
      .filter((row) => row.type === CART_LAYOUT_ROW_TYPES.PRODUCT)
      .map((row) => [String(row.cartLineId ?? row.orderItemId ?? ''), row.sortOrder])
  );
  const sectionSortOrderById = new Map(
    layout
      .filter((row) => row.type === CART_LAYOUT_ROW_TYPES.SECTION)
      .map((row) => [String(row.sectionId ?? ''), row.sortOrder])
  );

  return {
    ...payload,
    Products: (payload?.Products || []).map((product, index) => ({
      ...product,
      sortOrder: productSortOrderById.get(String(product?.cartLineId ?? product?.orderItemId ?? product?.OrderItemId ?? product?.id ?? '')) ?? index,
    })),
    cartSections: (payload?.cartSections || []).map((section, index) => ({
      ...section,
      sortOrder: sectionSortOrderById.get(String(section?.sectionId ?? section?.id ?? '')) ?? index,
    })),
    layout,
  };
};

export const addProductToPayload = (payload, product) => {
  const nextPayload = cloneCartPayload(payload);
  const source = nextPayload.meta?.source;
  const seed = deepClone(product || {});
  if (!seed.cartLineId) seed.cartLineId = uniqueWorkspaceId('product');
  const nextProduct = ensureCartProductIdentity(
    {
      ...seed,
      sortOrder: nextPayload.Products.length,
    },
    nextPayload.Products.length,
    source
  );
  const workspaceProduct = normalizeNewProductForCartWorkspace(nextProduct, { source });
  const normalizedProduct = normalizeCartPayload(
    {
      Products: [workspaceProduct],
      OtherService: [],
      cartSections: [],
      meta: nextPayload.meta,
    },
    {},
    source
  ).Products[0] || nextProduct;
  return applyLayoutSortOrders({
    ...nextPayload,
    Products: applySequentialProductSortOrder(
      [...nextPayload.Products, normalizedProduct],
      source
    ),
    cartSections: applySequentialSectionSortOrder(nextPayload.cartSections || []),
  });
};

export const updateProductInPayloadWithResult = (payload, productIdentity, patchOrProduct) => {
  const nextPayload = cloneCartPayload(payload);
  const { indexes: matchedIndexes, strategy: matchStrategy } = matchingProductIndexes(nextPayload.Products, productIdentity);
  const index = matchedIndexes.length === 1 ? matchedIndexes[0] : -1;
  if (index < 0) return { payload: nextPayload, matched: false, matchedIndex: -1, matchStrategy };
  const existing = nextPayload.Products[index] || {};
  const patch = deepClone(patchOrProduct || {});
  const merged = { ...existing, ...patch };
  for (const key of ['persistedOrderItemId', 'PersistedOrderItemId', 'sourceOrderItemId', 'SourceOrderItemId', 'originalOrderItemId', 'OriginalOrderItemId', 'dbItemId', 'DBItemId', 'OrderItemId', 'orderItemId', 'orderProductId', 'OrderProductId', 'id', 'cartLineId', 'CartLineId', 'lineId', 'LineId', 'sequence']) {
    if (existing[key] !== undefined && existing[key] !== null) merged[key] = existing[key];
  }
  if (existing.sortOrder !== undefined && existing.sortOrder !== null) merged.sortOrder = existing.sortOrder;
  const updated = ensureCartProductIdentity(merged, index, nextPayload.meta?.source);
  nextPayload.Products[index] = updated;
  if (process.env.NODE_ENV === 'development') console.info('[sales-order-cart-snapshot-product-save]', {
    editedOrderItemId: getSnapshotOrderItemIdentity(productIdentity),
    editedCartLineId: getSnapshotCartLineIdentity(productIdentity),
    editedProductId: productIdentity?.ProductId ?? productIdentity?.productId ?? null,
    editedSku: productIdentity?.SKU ?? productIdentity?.sku ?? null,
    beforeOrderItemIds: (payload?.Products || []).map(getSnapshotOrderItemIdentity),
    matchedIndexes,
    matchStrategy,
    matchedIndex: index,
    afterOrderItemIds: nextPayload.Products.map(getSnapshotOrderItemIdentity),
  });
  return {
    payload: applyLayoutSortOrders({
      ...nextPayload,
      Products: applySequentialProductSortOrder(
        [...nextPayload.Products],
        nextPayload.meta?.source
      ),
      cartSections: applySequentialSectionSortOrder(nextPayload.cartSections || []),
    }),
    matched: true,
    matchedIndex: index,
    matchStrategy,
  };
};

export const updateProductInPayload = (payload, productIdentity, patchOrProduct) =>
  updateProductInPayloadWithResult(payload, productIdentity, patchOrProduct).payload;

export const deleteProductFromPayload = (payload, productIdentity) => {
  const nextPayload = cloneCartPayload(payload);
  const index = findProductByIdentity(nextPayload.Products, productIdentity);
  if (index < 0) return nextPayload;
  return applyLayoutSortOrders({
    ...nextPayload,
    Products: applySequentialProductSortOrder(
      nextPayload.Products.filter((_, itemIndex) => itemIndex !== index),
      nextPayload.meta?.source
    ),
    cartSections: applySequentialSectionSortOrder(nextPayload.cartSections || []),
  });
};

export const copyProductInPayload = (payload, productIdentity) => {
  const nextPayload = cloneCartPayload(payload);
  const index = findProductByIdentity(nextPayload.Products, productIdentity);
  if (index < 0) return nextPayload;
  const sourceProduct = nextPayload.Products[index] || {};
  const clone = deepClone(sourceProduct);
  const copiedFromCartLineId = sourceProduct.cartLineId;
  delete clone.id;
  const sourceOrderItemId = firstPresent(
    sourceProduct.sourceOrderItemId,
    sourceProduct.SourceOrderItemId,
    sourceProduct.originalOrderItemId,
    sourceProduct.OriginalOrderItemId,
    sourceProduct.persistedOrderItemId,
    sourceProduct.PersistedOrderItemId,
    sourceProduct.orderItemId,
    sourceProduct.OrderItemId,
    sourceProduct.dbItemId,
    sourceProduct.DBItemId,
    sourceProduct.id
  );
  clone.sourceOrderItemId = sourceOrderItemId ?? null;
  clone.SourceOrderItemId = sourceOrderItemId ?? null;
  clone.originalOrderItemId = sourceOrderItemId ?? null;
  clone.OriginalOrderItemId = sourceOrderItemId ?? null;
  clone.persistedOrderItemId = null;
  clone.PersistedOrderItemId = null;
  clone.OrderItemId = null;
  clone.orderItemId = null;
  clone.dbItemId = null;
  clone.DBItemId = null;
  delete clone.orderProductId;
  delete clone.OrderProductId;
  const clearPersistedRowIdentity = (row = {}) => ({
    ...row,
    persistedOrderItemId: null,
    PersistedOrderItemId: null,
    OrderItemId: null,
    orderItemId: null,
    dbItemId: null,
    DBItemId: null,
  });
  if (Array.isArray(clone.pricings)) clone.pricings = clone.pricings.map(clearPersistedRowIdentity);
  if (Array.isArray(clone.Pricings)) clone.Pricings = clone.Pricings.map(clearPersistedRowIdentity);
  clone.meta = { ...(clone.meta || {}), copiedFromCartLineId };
  delete clone.cartLineId;
  clone.cartLineId = uniqueWorkspaceId('product-copy');
  const copied = ensureCartProductIdentity(clone, index + 1, nextPayload.meta?.source);
  const products = [...nextPayload.Products];
  products.splice(index + 1, 0, copied);
  return applyLayoutSortOrders({
    ...nextPayload,
    Products: applySequentialProductSortOrder(products, nextPayload.meta?.source),
    cartSections: applySequentialSectionSortOrder(nextPayload.cartSections || []),
  });
};

export const reorderProductsInPayload = (payload, activeIdentity, overIdentity) => {
  const nextPayload = cloneCartPayload(payload);
  const fromIndex = findProductByIdentity(nextPayload.Products, activeIdentity);
  const toIndex = findProductByIdentity(nextPayload.Products, overIdentity);
  if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return nextPayload;

  return applyLayoutSortOrders({
    ...nextPayload,
    Products: applySequentialProductSortOrder(
      arrayMove(nextPayload.Products, fromIndex, toIndex),
      nextPayload.meta?.source
    ),
    cartSections: applySequentialSectionSortOrder(nextPayload.cartSections || []),
  });
};

export const addSectionToPayload = (payload, title, style = 'light') => {
  const nextPayload = cloneCartPayload(payload);
  const normalizedTitle = normalizeSectionTitle(title);
  if (!normalizedTitle) return nextPayload;

  const nextSection = {
    sectionId: `section:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`,
    title: normalizedTitle,
    style: normalizeSectionStyle(style),
    sortOrder: buildCartLayout({
      products: nextPayload.Products,
      sections: nextPayload.cartSections,
    }).length,
  };

  return applyLayoutSortOrders({
    ...nextPayload,
    cartSections: [...(nextPayload.cartSections || []), nextSection],
  });
};

export const updateSectionInPayload = (payload, identity, title, style = 'light') => {
  const nextPayload = cloneCartPayload(payload);
  const index = findByIdentity(nextPayload.cartSections, identity, SECTION_MATCH_KEYS);
  if (index < 0) return nextPayload;
  nextPayload.cartSections[index] = {
    ...nextPayload.cartSections[index],
    title: normalizeSectionTitle(title),
    style: normalizeSectionStyle(style),
  };
  return applyLayoutSortOrders(nextPayload);
};

export const deleteSectionFromPayload = (payload, identity) => {
  const nextPayload = cloneCartPayload(payload);
  const index = findByIdentity(nextPayload.cartSections, identity, SECTION_MATCH_KEYS);
  if (index < 0) return nextPayload;
  return applyLayoutSortOrders({
    ...nextPayload,
    cartSections: nextPayload.cartSections.filter((_, rowIndex) => rowIndex !== index),
  });
};

export const reorderCartLayoutInPayload = (payload, activeLayoutId, overLayoutId) => {
  const inputLayout = buildCartLayout({
    products: payload?.Products,
    sections: payload?.cartSections,
  });
  const nextPayload = cloneCartPayload(payload);
  const layout = buildCartLayout({
    products: nextPayload.Products,
    sections: nextPayload.cartSections,
  });
  const resolveNormalizedLayoutId = (requestedLayoutId) => {
    if (layout.some((row) => row.layoutId === requestedLayoutId)) return requestedLayoutId;
    const inputMatches = inputLayout.filter((row) => row.layoutId === requestedLayoutId);
    if (inputMatches.length !== 1 || inputMatches[0].type !== CART_LAYOUT_ROW_TYPES.PRODUCT) {
      return requestedLayoutId;
    }
    const normalizedMatches = layout.filter((row) =>
      String(row.orderItemId) === String(inputMatches[0].orderItemId)
    );
    return normalizedMatches.length === 1 ? normalizedMatches[0].layoutId : requestedLayoutId;
  };
  const fromIndex = layout.findIndex((row) => row.layoutId === resolveNormalizedLayoutId(activeLayoutId));
  const toIndex = layout.findIndex((row) => row.layoutId === resolveNormalizedLayoutId(overLayoutId));
  if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return nextPayload;

  const reorderedLayout = arrayMove(layout, fromIndex, toIndex).map((row, index) => ({
    ...row,
    sortOrder: index,
  }));

  const productSortOrderByKey = new Map(
    reorderedLayout
      .filter((row) => row.type === CART_LAYOUT_ROW_TYPES.PRODUCT)
      .map((row) => [String(row.cartLineId ?? row.orderItemId ?? ''), row.sortOrder])
  );
  const sectionSortOrderByKey = new Map(
    reorderedLayout
      .filter((row) => row.type === CART_LAYOUT_ROW_TYPES.SECTION)
      .map((row) => [String(row.sectionId ?? ''), row.sortOrder])
  );

  return {
    ...nextPayload,
    Products: nextPayload.Products.map((product, index) => ({
      ...product,
      sortOrder: productSortOrderByKey.get(String(product?.cartLineId ?? product?.orderItemId ?? product?.OrderItemId ?? product?.id ?? '')) ?? index,
    })),
    cartSections: (nextPayload.cartSections || []).map((section, index) => ({
      ...section,
      sortOrder: sectionSortOrderByKey.get(String(section?.sectionId ?? section?.id ?? '')) ?? index,
    })),
    layout: reorderedLayout,
  };
};

export const addOtherServiceToPayload = (payload, service) => {
  const nextPayload = cloneCartPayload(payload);
  const seed = deepClone(service || {});
  if (!seed.cartServiceId) seed.cartServiceId = uniqueWorkspaceId('service');
  const nextService = ensureCartServiceIdentity(seed, nextPayload.OtherService.length, nextPayload.meta?.source);
  return { ...nextPayload, OtherService: [...nextPayload.OtherService, nextService] };
};

export const updateOtherServiceInPayload = (payload, serviceIdentity, patchOrService) => {
  const nextPayload = cloneCartPayload(payload);
  const index = findByIdentity(nextPayload.OtherService, serviceIdentity, SERVICE_MATCH_KEYS);
  if (index < 0) return nextPayload;
  const existing = nextPayload.OtherService[index] || {};
  const patch = deepClone(patchOrService || {});
  const updated = ensureCartServiceIdentity({ ...existing, ...patch }, index, nextPayload.meta?.source);
  nextPayload.OtherService[index] = updated;
  return { ...nextPayload, OtherService: [...nextPayload.OtherService] };
};

export const deleteOtherServiceFromPayload = (payload, serviceIdentity) => {
  const nextPayload = cloneCartPayload(payload);
  const index = findByIdentity(nextPayload.OtherService, serviceIdentity, SERVICE_MATCH_KEYS);
  if (index < 0) return nextPayload;
  return {
    ...nextPayload,
    OtherService: nextPayload.OtherService.filter((_, itemIndex) => itemIndex !== index),
  };
};

const adjustmentIdentity = (adjustment = {}) =>
  String(adjustment?.id || adjustment?.adjustmentId || "").trim();

export const addAdjustmentToPayload = (payload, adjustment = {}) => {
  const nextPayload = cloneCartPayload(payload);
  const nextAdjustment = deepClone(adjustment);
  nextAdjustment.id = adjustmentIdentity(nextAdjustment) || uniqueWorkspaceId("adj").replace(/:/g, "_");
  return {
    ...nextPayload,
    Adjustments: [...(nextPayload.Adjustments || []), nextAdjustment],
  };
};

export const updateAdjustmentInPayload = (payload, adjustment = {}) => {
  const nextPayload = cloneCartPayload(payload);
  const id = adjustmentIdentity(adjustment);
  if (!id) return nextPayload;
  return {
    ...nextPayload,
    Adjustments: (nextPayload.Adjustments || []).map((row) =>
      adjustmentIdentity(row) === id ? { ...row, ...deepClone(adjustment), id } : row
    ),
  };
};

export const deleteAdjustmentFromPayload = (payload, identity) => {
  const nextPayload = cloneCartPayload(payload);
  const id = typeof identity === "object" ? adjustmentIdentity(identity) : String(identity || "").trim();
  if (!id) return nextPayload;
  return {
    ...nextPayload,
    Adjustments: (nextPayload.Adjustments || []).filter((row) => adjustmentIdentity(row) !== id),
  };
};
